import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import accepts from 'attr-accept';
import toArray from 'lodash/toArray';
import getConditionalClasses, {
  CORE_CLASSNAME
} from '@adsk/bim360-matrix-react-helpers/es/getConditionalClasses';

import './_Dropzone.scss';

/**
 * Dropzone component.
 * This component handles files drag & drop - changing styles when drop is available & runs a callback onDrop.
 */
export default class Dropzone extends PureComponent {
  /**
   * Prevent default document drag over
   * @param e
   */
  static onDocumentDragOver(e) {
    e.preventDefault();
  }

  /**
   *
   * @param props
   */
  constructor(props) {
    super(props);

    this.state = {
      dragsEnterCounter: 0
    };

    this._handleDrop = this._handleDrop.bind(this);
    this._handleDragEnter = this._handleDragEnter.bind(this);
    this._handleDragLeave = this._handleDragLeave.bind(this);
    this._handleDragExit = this._handleDragExit.bind(this);
    this.onDocumentDrop = this.onDocumentDrop.bind(this);
  }

  /**
   * componentDidMount
   */
  componentDidMount() {
    document.addEventListener('dragover', Dropzone.onDocumentDragOver);
    document.addEventListener('drop', this.onDocumentDrop);
  }

  /**
   * Handle drop only on Dropzone component
   * @param e
   */
  onDocumentDrop(e) {
    if (this.node.contains(e.target)) {
      // if we intercepted an event for our instance, let it propagate down to the instance's onDrop handler
      return;
    }
    e.preventDefault();
  }

  /**
   * Handle drop
   * @param e
   * @private
   */
  _handleDrop(e) {
    e.preventDefault();
    this.setState({ dragsEnterCounter: 0 });
    if (e.dataTransfer.files.length > 0) {
      const filesArray = toArray(e.dataTransfer.files);
      if (!filesArray.find(file => !accepts(file, this.props.accept))) {
        this.props.onDrop(Array.prototype.slice.call(e.dataTransfer.files));
      }
    }
  }

  /**
   * Handle drag enter.
   * Count number of drag enters to know when we actually leave the component.
   * @param e
   * @private
   */
  _handleDragEnter(e) {
    e.preventDefault();
    this.setState({ dragsEnterCounter: this.state.dragsEnterCounter + 1 });
  }

  /**
   * Handle drag leave.
   * Decrement number of drag enters to know when we actually leave the component.
   * @private
   */
  _handleDragLeave() {
    if (this.state.dragsEnterCounter) {
      this.setState({ dragsEnterCounter: this.state.dragsEnterCounter - 1 });
    }
  }

  /**
   * Handle drag exit.
   * Decrement number of drag enters to know when we actually leave the component.
   * @private
   */
  _handleDragExit() {
    if (this.state.dragsEnterCounter) {
      this.setState({ dragsEnterCounter: this.state.dragsEnterCounter - 1 });
    }
  }

  /**
   * Get handler if Dropzone is enabled
   * @param handler
   * @returns {*}
   */
  getHandler(handler) {
    if (this.props.disabled) {
      return null;
    }
    return handler;
  }

  /**
   * componentWillUnmount - clears event listeners
   */
  componentWillUnmount() {
    document.removeEventListener('dragover', Dropzone.onDocumentDragOver, false);
    document.removeEventListener('drop', this.onDocumentDrop, false);
  }

  /**
   * render
   * @returns {XML}
   */
  render() {
    const coreClasses = ['Dropzone', this.props.className];

    const classes = classNames(
      coreClasses,
      getConditionalClasses(coreClasses, {
        [`${CORE_CLASSNAME}--accept`]: this.state.dragsEnterCounter
      })
    );

    return (
      <div
        className={classes}
        style={this.props.style}
        onDrop={this.getHandler(this._handleDrop)}
        onDragEnter={this.getHandler(this._handleDragEnter)}
        onDragLeave={this.getHandler(this._handleDragLeave)}
        onDragExit={this.getHandler(this._handleDragExit)}
        ref={n => {
          this.node = n;
        }}
      >
        {this.props.children}
      </div>
    );
  }
}

Dropzone.defaultProps = {
  disabled: false
};

Dropzone.propTypes = {
  /**
   * Allow specific types of files. See https://github.com/okonet/attr-accept for more information.
   * Keep in mind that mime type determination is not reliable across platforms. CSV files,
   * for example, are reported as text/plain under macOS but as application/vnd.ms-excel under
   * Windows. In some cases there might not be a mime type set at all.
   * See: https://github.com/react-dropzone/react-dropzone/issues/276
   */
  accept: PropTypes.string,
  /**
   * Enable/disable the dropzone entirely
   */
  disabled: PropTypes.bool,
  /**
   * onDrop callback
   */
  onDrop: PropTypes.func.isRequired,
  /**
   * Contents of the dropzone
   */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  /**
   * Style object
   */
  style: PropTypes.object,
  /**
   * Core class name that's applied to the container, also get's all the BEM modifiers the input can be in like, "--accept"
   */
  className: PropTypes.string
};
